import * as angular from 'angular';
import * as _ from 'lodash';

import { wtDroppableDrop, seriesAdded, toDictionary, blackTextColor, whiteTextColor } from '../../common/';
import { ISeriesSelectorService } from '../../services';
import { ComponentDefinition, ITileSerieConfiguration, ISortConfiguration, Dictionary, IValueViewModel, ObjectType } from '../../models';

interface TileSeriesTuple {
    isBindable: boolean;
    series: ITileSerieConfiguration;
    viewModel: IValueViewModel;
}


class TileSeriesController implements angular.IComponentController {
    isLoading: boolean;
    series: ITileSerieConfiguration[];
    sortConfig: ISortConfiguration;
    tuples: TileSeriesTuple[];
    viewModels: IValueViewModel[];

    private _viewModels: Dictionary<IValueViewModel>;

    selectableValues = [
        { key: 'groupName', value: 'Group name' },
        { key: 'name', value: 'Name' },
        { key: 'unitName', value: 'Unit name' },
        { key: 'value', value: 'Value' }
    ];

    static $inject = ['$scope', 'seriesSelectorService'];

    constructor(
        private _$scope: angular.IScope,
        private _seriesSelectorService: ISeriesSelectorService,
    ) {
    }

    $onInit() {
        if (!this.series) {
            this.series = [];
        } else {
            this.mapVms(this.series);
        }

        this.sortConfig = {
            animation: 150,
            handle: '.drag-handle',
            onSort: evt => this.sort(evt),
        };

        this._$scope.$on(wtDroppableDrop, (event, data) => {
            if (data && data.itemId) {
                // select+valueType+guid
                if (!data.binId || !this.updateSerie(data.binId, data.itemId)) {
                    this.addSeries(data.itemId);
                }
            }
        });

        this._$scope.$on(seriesAdded, (event, data) => {
            if (data && data.guid) {
                this.addSeries(data.guid);
            }
        });

        this._$scope.$watch(() => this.series, () => {
            this.mapVms(this.series);
        });
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['viewModels']?.currentValue) {
            this._viewModels = toDictionary(this.viewModels);
            this.mapVms(this.series ?? []);
        }
    }

    deleteSerie(serie: any) {
        this.series = this.series.filter(s => s !== serie);
        this.tuples = this.tuples.filter(t => t.series !== serie);
    }

    private mapVms(series: ITileSerieConfiguration[]) {
        if (this._viewModels) {
            this.tuples = series.map(s => {
                const viewModel = this._viewModels[s.guid];
                const isBindable = viewModel.objectType === ObjectType.Identifier;
                return {
                    isBindable,
                    series: s,
                    viewModel
                };
            });
        }
    }

    private updateSerie(binId: string, guid: string): boolean {
        const ids = binId.split('+');
        const serie = _.find(this.series, s => s.guid === ids[2] && s.selection === ids[0] &&
            (s.selection !== 'value' || s.valueType === ids[1]));

        if (serie) {
            const tuple = _.find(this.tuples, t => t.series === serie);

            const newVm = this._viewModels[guid];
            const isIdentifier = newVm.objectType === ObjectType.Identifier;
            serie.guid = guid;
            serie.isBoundToGroup = isIdentifier && serie.isBoundToGroup;

            const viewModel = newVm;
            const isBindable = isIdentifier;

            if (serie.selection === 'value') {
                const valueTypes = this._seriesSelectorService.getValues(viewModel);
                if (!_.some(valueTypes, vt => vt.key === serie.valueType)) {
                    serie.valueType = this.getNextValueType(viewModel);
                }
            }
            if (tuple) {
                tuple.isBindable = isBindable;
                tuple.viewModel = viewModel;
            }

            return true;
        }

        return false;
    }

    private addSeries(guid: string) {
        const vm = this._viewModels[guid];
        const valueType = this.getNextValueType(vm);
        const selection = valueType
            ? 'value'
            : 'name';
        const s = {
            guid: guid,
            relativeFontSize: 1,
            foreground: blackTextColor,
            background: whiteTextColor,
            selection: selection,
            valueType: valueType,
            showAlarm: false,
            decimals: 2,
            isBoundToGroup: false
        };
        this.series.push(s);

        this.tuples.push({
            isBindable: vm.objectType === ObjectType.Identifier,
            series: s,
            viewModel: vm
        });
    }

    private getNextValueType(vm: IValueViewModel): string {
        const existings = angular.copy(this.series).filter(s => s.selection === 'value');
        return this._seriesSelectorService
            .getAvailableValueType(vm, existings.map(e => { return { guid: e.guid, value: e.valueType }; }));
    }

    private sort(evt) {
        evt.models.forEach((s, i) => {
            s.series.sortOrder = i;
        });
        this.tuples = angular.copy(evt.models);
        this.series = this.tuples.map(d => d.series);
    }
}

export const wtTileSeries: ComponentDefinition = {
    componentId: 'wtTileSeries',
    controller: TileSeriesController,
    template: require('./tileSeries.component.html'),
    controllerAs: 'vm',
    bindings: {
        series: '=',
        isBindable: '<?',
        viewModels: '<'
    }
};